'use client'

import { Fragment } from 'react'

import { InfoCircle16 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Icon, Label, Spacer, Text, Validation } from '@vinted/web-ui'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  EscrowRefundEstimateElementModel,
  EscrowRefundEstimateModel,
  RefundElementsWithAction,
} from '@marketplace-web/complaints/complaints-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

type Props = {
  isOpen: boolean
  refundEstimate: EscrowRefundEstimateModel | null | undefined
  isRefundEstimateLoading: boolean
  onCurrencyConversionClick: () => void
  onBuyerProtectionFeeInfoClick: () => void
  onSubmit: () => void
  onClose: () => void
}

const AcceptPartialRefundOfferDialog = ({
  isOpen,
  refundEstimate,
  isRefundEstimateLoading,
  onBuyerProtectionFeeInfoClick,
  onCurrencyConversionClick,
  onSubmit,
  onClose,
}: Props) => {
  const translate = useTranslate('accept_partial_refund_offer_dialog')
  const { locale } = useIntl()

  const { refundElements } = refundEstimate || {}

  const renderBody = () => (
    <Cell>
      <Cell
        fullWidthTitle
        styling="tight"
        title={
          <Text
            as="h1"
            text={translate('title')}
            type="heading"
            width="parent"
            alignment="center"
          />
        }
      />
      <Spacer size="large" />
      <Cell styling="tight" body={translate('body')} />
    </Cell>
  )

  const renderCurrencyConversionNote = () => {
    if (!refundEstimate?.beforeConversion) return null

    return (
      <button type="button" onClick={onCurrencyConversionClick}>
        <Text
          as="p"
          clickable
          theme="inverse"
          type="caption"
          text={translate('learn_more_about_currency_conversion')}
        />
      </button>
    )
  }

  const renderBuyerProtectionFeeInfo = () => (
    <div className="u-flexbox">
      <Text as="span" type="subtitle" text={translate('buyer_protection_fee_refund')} />
      <Spacer orientation="vertical" />
      <button
        className="u-flexbox u-align-self-center"
        type="button"
        onClick={onBuyerProtectionFeeInfoClick}
        aria-label={translate('a11y.buyer_protection_fee_refund_info_icon')}
      >
        <Icon name={InfoCircle16} color="greyscale-level-2" />
      </button>
    </div>
  )

  const renderRefundElement = (element: EscrowRefundEstimateElementModel) => {
    switch (element.type) {
      case RefundElementsWithAction.BuyerProtectionFee:
        return (
          <Cell
            styling="tight"
            body={renderBuyerProtectionFeeInfo()}
            suffix={formatCurrencyAmount(element.money, locale)}
          />
        )
      default:
        return (
          <Cell
            styling="tight"
            body={
              <>
                {element.title && <Text as="span" type="subtitle" text={element.title} />}
                {element.error && <Validation text={element.error} theme="warning" />}
              </>
            }
            suffix={formatCurrencyAmount(element.money, locale)}
          />
        )
    }
  }

  const renderRefundElements = () => {
    if (!refundElements) return null

    return refundElements.map((element, index) => (
      <Fragment key={index}>{renderRefundElement(element)}</Fragment>
    ))
  }

  const renderRefundSummary = () => {
    if (!refundEstimate) return null

    return (
      <>
        <Label text={translate('summary')} type="stacked" />
        <Cell>
          {renderRefundElements()}
          <Spacer size="medium" />
          <Cell
            styling="tight"
            title={
              refundEstimate.beforeConversion
                ? translate('total_multicurrency_refund')
                : translate('total_refund')
            }
            suffix={
              <Text
                as="h2"
                type="title"
                text={formatCurrencyAmount(refundEstimate.totalEstimatedRefund, locale)}
              />
            }
          />
          {renderCurrencyConversionNote()}
        </Cell>
      </>
    )
  }

  const renderActions = () => (
    <Cell>
      <Button text={translate('actions.accept')} styling="filled" onClick={onSubmit} />
      <Spacer size="large" />
      <Button text={translate('actions.cancel')} styling="flat" onClick={onClose} />
    </Cell>
  )

  const renderContent = () => {
    if (isRefundEstimateLoading)
      return <ContentLoader testId="accept-partial-refund-offer-dialog-loader" />

    return (
      <>
        {renderBody()}
        {renderRefundSummary()}
        {renderActions()}
      </>
    )
  }

  return (
    <Dialog show={isOpen} hasScrollableContent>
      <div className="u-fill-width">
        <ScrollableArea>{renderContent()}</ScrollableArea>
      </div>
    </Dialog>
  )
}

export default AcceptPartialRefundOfferDialog
